import type {
  CfAccessIdentity,
  CreateSandboxRequest,
  QueryResult,
  SandboxInfo,
} from "./types";

// Same-origin path served by cf-pages/functions/api/[[path]].ts which
// adds the Bearer token server-side and forwards to the simdb backend.
// The browser never sees the API key.
export const API_BASE = "/api";

export class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  base: string = API_BASE
): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let detail = body;
    try {
      const parsed = JSON.parse(body) as { detail?: string };
      if (parsed?.detail) detail = parsed.detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, body, detail || `HTTP ${res.status}`);
  }

  // DELETE may return {deleted:true}; JSON parse handles both
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const api = {
  listSandboxes: (): Promise<SandboxInfo[]> => request("/sandboxes"),

  getSandbox: (id: string): Promise<SandboxInfo> =>
    request(`/sandboxes/${encodeURIComponent(id)}`),

  createSandbox: (req: CreateSandboxRequest): Promise<SandboxInfo> =>
    request("/sandboxes", {
      method: "POST",
      body: JSON.stringify(req),
    }),

  deleteSandbox: (id: string): Promise<{ deleted: boolean }> =>
    request(`/sandboxes/${encodeURIComponent(id)}`, { method: "DELETE" }),

  runQuery: (id: string, sql: string): Promise<QueryResult> =>
    request(`/sandboxes/${encodeURIComponent(id)}/query`, {
      method: "POST",
      body: JSON.stringify({ sql }),
    }),

  // Cloudflare Access identity — absolute URL, NOT prefixed with API_BASE
  getIdentity: async (): Promise<CfAccessIdentity | null> => {
    try {
      const res = await fetch("/cdn-cgi/access/get-identity", {
        credentials: "include",
      });
      if (!res.ok) return null;
      return (await res.json()) as CfAccessIdentity;
    } catch {
      return null;
    }
  },
};
